"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import type { ThreadFilterType } from "skedyul-ui/src/components/ui/ThreadsSidebar";
import type { AppDispatch, RootState } from "@store/index";
import {
  selectThreadCurrent,
  setThreadSidebarFilter,
  setThreadSidebarContactSort,
  type ThreadSidebarContactSort,
} from "@store/features/threads/threadSlice";

const THREAD_SIDEBAR_FILTER_KEY = "thread-sidebar-filter";
const THREAD_SIDEBAR_CONTACT_SORT_KEY = "threads-sidebar-contact-sort";

function readStoredThreadSidebarFilter(): ThreadFilterType | null {
  if (typeof window === "undefined") return null;
  try {
    const value = sessionStorage.getItem(THREAD_SIDEBAR_FILTER_KEY);
    if (value === "inbox" || value === "conversations") return value;
  } catch {
    // ignore storage errors
  }
  return null;
}

function writeStoredThreadSidebarFilter(filter: ThreadFilterType): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(THREAD_SIDEBAR_FILTER_KEY, filter);
  } catch {
    // ignore storage errors
  }
}

function readStoredThreadSidebarContactSort(): ThreadSidebarContactSort | null {
  if (typeof window === "undefined") return null;
  try {
    const value = sessionStorage.getItem(THREAD_SIDEBAR_CONTACT_SORT_KEY);
    if (value === "last_contact" || value === "last_activity") return value;
    if (!value) return null;
    const parsed: unknown = JSON.parse(value);
    if (parsed === "last_contact" || parsed === "last_activity") return parsed;
  } catch {
    // ignore storage errors / invalid JSON
  }
  return null;
}

function writeStoredThreadSidebarContactSort(
  sort: ThreadSidebarContactSort,
): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(THREAD_SIDEBAR_CONTACT_SORT_KEY, sort);
  } catch {
    // ignore storage errors
  }
}

/**
 * Restores the sidebar Inbox/Conversations tab on mount and persists manual changes.
 * On remount/reload, switches to Conversations when the open thread has a routing
 * participant; otherwise restores the last selected tab (defaults to Inbox).
 * Does not auto-switch tabs when navigating between threads within a session.
 */
export function useSyncThreadSidebarFilter(): void {
  const pathname = usePathname();
  const dispatch = useDispatch<AppDispatch>();
  const sidebarFilter = useSelector(
    (state: RootState) => state.threads.sidebarFilter,
  );
  const sidebarContactSort = useSelector(
    (state: RootState) => state.threads.sidebarContactSort,
  );
  const currentThread = useSelector(selectThreadCurrent);
  const threadItems = useSelector((state: RootState) => state.threads.items);
  const listLoading = useSelector(
    (state: RootState) => state.threads.listLoading,
  );
  const detailLoading = useSelector(
    (state: RootState) => state.threads.detailLoading,
  );

  const threadPathMatch = pathname.match(/\/threads\/([^/?]+)/);
  const selectedThreadId = threadPathMatch?.[1] ?? null;

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) return;

    const storedFilter = readStoredThreadSidebarFilter() ?? "inbox";
    const storedContactSort =
      readStoredThreadSidebarContactSort() ?? "last_activity";
    dispatch(setThreadSidebarContactSort(storedContactSort));

    if (!selectedThreadId || selectedThreadId === "new") {
      dispatch(setThreadSidebarFilter(storedFilter));
      hasInitializedRef.current = true;
      return;
    }

    const thread =
      currentThread?.id === selectedThreadId
        ? currentThread
        : threadItems.find((item) => item.id === selectedThreadId);

    if (!thread) {
      if (listLoading || detailLoading) {
        return;
      }
      dispatch(setThreadSidebarFilter(storedFilter));
      hasInitializedRef.current = true;
      return;
    }

    const filter = thread.routingParticipantId ? "conversations" : storedFilter;
    dispatch(setThreadSidebarFilter(filter));
    hasInitializedRef.current = true;
  }, [
    selectedThreadId,
    currentThread,
    threadItems,
    listLoading,
    detailLoading,
    dispatch,
  ]);

  useEffect(() => {
    if (!hasInitializedRef.current) return;
    writeStoredThreadSidebarFilter(sidebarFilter);
  }, [sidebarFilter]);

  useEffect(() => {
    if (!hasInitializedRef.current) return;
    writeStoredThreadSidebarContactSort(sidebarContactSort);
  }, [sidebarContactSort]);
}
